import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/layout/PageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { InternalTesting } from './InternalTesting';
import { EndpointTesting } from './EndpointTesting';

type DeviceTab = 'network' | 'endpoints';

function isDeviceTab(value: string | null): value is DeviceTab {
  return value === 'network' || value === 'endpoints';
}

/**
 * Unified "Devices" page (navigation simplification, FIX3) — replaces the separate Internal
 * Testing and Endpoint Testing pages/nav items with two tabs under one shared header.
 * InternalTesting/EndpointTesting no longer render their own PageHeader (this page's header
 * covers both), but are otherwise unchanged: same Start Scan dialog, Agent capabilities,
 * Authorized Exploitation, and Endpoint checks sections as before.
 *
 * `?tab=` supports deep-linking (e.g. the retired /internal-testing and /endpoint-testing routes
 * redirect here with the matching tab pre-selected) without needing to persist tab state anywhere
 * beyond the URL.
 */
export function Devices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: DeviceTab = isDeviceTab(requestedTab) ? requestedTab : 'network';

  const handleTabChange = (value: string) => {
    if (!isDeviceTab(value)) return;
    setSearchParams(value === 'network' ? {} : { tab: value }, { replace: true });
  };

  return (
    <div>
      <PageHeader title="Devices" description="Manage agents and scan your internal network and employee devices." />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
        </TabsList>
        <TabsContent value="network">
          <InternalTesting />
        </TabsContent>
        <TabsContent value="endpoints">
          <EndpointTesting />
        </TabsContent>
      </Tabs>
    </div>
  );
}
